import { ActionIcon, Button } from '@mantine/core';
import { sizes, variants, Icon, toastNotifications } from '@uag/react-core';
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Rnd } from 'react-rnd';

const dialogWidth = 430;
const dialogHeight = 310;

type SaveProjectDialogProps = {
    isPending: boolean;
    saveName: string;
    saveDescription: string;
    saveError: string | null;
    onClose: () => void;
    onNameChange: (value: string) => void;
    onDescriptionChange: (value: string) => void;
    onSave: () => void;
};

export const SaveProjectDialog = ({
    isPending,
    saveName,
    saveDescription,
    saveError,
    onClose,
    onNameChange,
    onDescriptionChange,
    onSave
}: SaveProjectDialogProps) => {
    const { t } = useTranslation();

    const isProjectNameEmpty = useMemo(() => saveName.trim().length === 0, [saveName]);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleSetFocusOnInput = useCallback(() => {
        requestAnimationFrame(() => {
            const input = inputRef.current;
            if (input) {
                input.focus();
                input.setSelectionRange(input.value.length, input.value.length);
            }
        });
    }, []);

    useEffect(() => {
        handleSetFocusOnInput();
    }, [handleSetFocusOnInput]);

    const handleOnSaveProject = useCallback(() => {
        if (isProjectNameEmpty) {
            toastNotifications.information({ title: t('info'), message: t('infoSaveProject') });
            return;
        }

        onSave();
    }, [isProjectNameEmpty, onSave, t]);

    const handleNameInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter' && !isProjectNameEmpty) {
                handleOnSaveProject();
            }
        },
        [isProjectNameEmpty, handleOnSaveProject]
    );

    return (
        <div
            style={{
                height: '100vh',
                left: 0,
                pointerEvents: 'none',
                position: 'fixed',
                top: 0,
                width: '100vw',
                zIndex: 9999
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerMove={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
        >
            <Rnd
                bounds="window"
                default={{
                    x: Math.max((window.innerWidth - dialogWidth) / 2, 0),
                    y: Math.max((window.innerHeight - dialogHeight) / 2, 0),
                    width: dialogWidth,
                    height: dialogHeight
                }}
                dragHandleClassName="rnd-drag-handle"
                enableResizing={false}
                enableUserSelectHack={false}
                size={{ width: dialogWidth, height: dialogHeight }}
                style={{ pointerEvents: 'auto' }}
            >
                <div
                    style={{
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 6,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                        color: '#374151',
                        display: 'flex',
                        flexDirection: 'column',
                        fontFamily: 'inherit',
                        fontSize: 12,
                        height: dialogHeight,
                        userSelect: 'none',
                        width: dialogWidth
                    }}
                >
                    {/* Drag handle header */}
                    <div
                        className="rnd-drag-handle"
                        style={{
                            alignItems: 'center',
                            background: '#f3f4f6',
                            borderBottom: '1px solid #e5e7eb',
                            borderRadius: '6px 6px 0 0',
                            cursor: 'move',
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '6px 10px'
                        }}
                    >
                        <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: 0.5 }}>
                            {t('labelSaveProjectAs')}
                        </span>
                        <ActionIcon
                            className="action-icon-small bg-background-gray-ultra-light hover:bg-background-gray! pointer-events-auto icon-sm"
                            onClick={onClose}
                            onPointerDown={(e) => e.stopPropagation()}
                        >
                            close
                        </ActionIcon>
                    </div>

                    {/* Body */}
                    <div className="space-y-3 p-3" style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
                        <div>
                            <span className="text-xs font-medium text-text-light uppercase">
                                {t('labelProjectName')}
                                {isProjectNameEmpty && (
                                    <span className="normal-case" style={{ color: '#f59e0b', marginLeft: 6 }}>
                                        <Icon
                                            className="action-menu-entry-decoration action-menu-entry-decoration-icon mr-0.5"
                                            icon="warning"
                                        />
                                        {t('infoRequired')}
                                    </span>
                                )}
                            </span>

                            <input
                                ref={inputRef}
                                aria-label="saveProjectName"
                                className="h-8 w-full rounded-sm border border-border-light px-2 text-sm text-text-default outline-none focus:border-border-accent"
                                maxLength={200}
                                value={saveName}
                                onChange={(event) => onNameChange(event.target.value)}
                                onKeyDown={handleNameInputKeyDown}
                            />
                        </div>

                        <div className="mt-4">
                            <div className="mb-1 text-xs font-medium uppercase text-text-light">
                                {t('labelDescription')}
                                <span className="normal-case" style={{ marginLeft: 6 }}>
                                    {t('infoOptional')}
                                </span>
                            </div>
                            <textarea
                                aria-label="saveProjectDescription"
                                className="focus:border-border-accent text-sm text-text-default outline-none w-full rounded-sm border border-border-light"
                                maxLength={1000}
                                placeholder={t('placeholderDescription')}
                                rows={3}
                                style={{
                                    height: 100,
                                    resize: 'none',
                                    border: '1px solid #d1d5db',
                                    hyphens: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'break-word',
                                    wordBreak: 'break-word',
                                    borderRadius: 4,
                                    padding: '6px 8px',
                                    fontSize: 13,
                                    color: '#111827',
                                    background: '#fff',
                                    outline: 'none',
                                    fontFamily: 'inherit'
                                }}
                                value={saveDescription}
                                onChange={(event) => onDescriptionChange(event.target.value)}
                            />
                        </div>

                        {saveError && <div className="text-xs text-text-danger">{saveError}</div>}

                        <div className="flex items-center justify-end gap-2">
                            <Button size={sizes.button.small} variant={variants.button.secondary} onClick={onClose}>
                                {t('cancel')}
                            </Button>
                            <Button
                                aria-disabled={isPending || isProjectNameEmpty}
                                className={`min-w-17 flex items-center justify-center
                                    ${isPending || isProjectNameEmpty ? 'bg-background-disabled cursor-not-allowed text-text-disabled' : ''}
                                `}
                                loading={isPending}
                                size={sizes.button.small}
                                onClick={handleOnSaveProject}
                            >
                                <span style={{ opacity: isPending ? 0 : 1 }}>{t('save')}</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </Rnd>
        </div>
    );
};
